import React, { useState, useEffect } from "react";
import { getMatchesByTournamentId } from "services/matchService";
import * as Sentry from "@sentry/react";

const PriorityMatches = ({ tournament, user }) => {
  const [priorityMatches, setPriorityMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMatches = async () => {
      if (!tournament?.id) return;

      try {
        const allMatches = await getMatchesByTournamentId(tournament.id);
        calculatePriorityMatches(allMatches);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error fetching matches for priority:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [tournament?.id]);

  const calculatePriorityMatches = (allMatches) => {
    // Filter matches that haven't been played yet
    const unplayedMatches = allMatches.filter(match => !match.result);

    // Calculate priority and remaining matches for each team
    const matchesWithPriority = unplayedMatches
      .filter(match => {
        const team1Name = getTeamDisplayName(match, "team1");
        const team2Name = getTeamDisplayName(match, "team2");

        // Skip BYE matches and placeholder matches
        return !(team1Name === "BYE" || team2Name === "BYE" ||
                team1Name === "?" || team2Name === "?" ||
                team1Name.includes("Gagnant") || team1Name.includes("Perdant") ||
                team2Name.includes("Gagnant") || team2Name.includes("Perdant"));
      })
      .map(match => {
        const team1Name = getTeamDisplayName(match, "team1");
        const team2Name = getTeamDisplayName(match, "team2");

        const team1Remaining = countRemainingMatches(team1Name, allMatches);
        const team2Remaining = countRemainingMatches(team2Name, allMatches);
        const priority = team1Remaining + team2Remaining;

        return {
          ...match,
          priority,
          team1Remaining,
          team2Remaining,
          team1Name,
          team2Name
        };
      });

    // Sort by priority (descending)
    const sortedMatches = matchesWithPriority.sort((a, b) => b.priority - a.priority);

    // Filter to ensure each team appears at most once
    const selectedMatches = [];
    const usedTeams = new Set();

    for (const match of sortedMatches) {
      if (selectedMatches.length >= 6) break;

      if (!usedTeams.has(match.team1Name) && !usedTeams.has(match.team2Name)) {
        selectedMatches.push(match);
        usedTeams.add(match.team1Name);
        usedTeams.add(match.team2Name);
      }
    }

    setPriorityMatches(selectedMatches);
  };


  const countRemainingMatches = (teamName, allMatches) => {
    return allMatches.filter(match => {
      if (match.result) return false; // Skip completed matches

      const team1Name = getTeamDisplayName(match, "team1");
      const team2Name = getTeamDisplayName(match, "team2");

      return team1Name === teamName || team2Name === teamName;
    }).length;
  };

  const getTeamDisplayName = (match, teamKey) => {
    if (match[teamKey]?.players) {
      return match[teamKey].players.map((p) => p.name).join(" & ");
    }
    return match[`${teamKey}Name`] || "BYE";
  };

  const isUserInMatch = (match) => {
    if (!user?.uid) return false;

    const isInTeam1 = match.team1?.players?.some(p => p.id === user.uid);
    const isInTeam2 = match.team2?.players?.some(p => p.id === user.uid);

    return isInTeam1 || isInTeam2;
  };

  if (loading) {
    return (
      <div className="bg-slate-800 p-3 rounded-md mb-4">
        <p className="text-sm text-slate-200">Chargement des prochains matchs...</p>
      </div>
    );
  }

  if (priorityMatches.length === 0) {
    return null; // Don't show anything if no priority matches
  }

  const matchFormat = priorityMatches.length > 0 && priorityMatches[0].goalsToWin
    ? `🎯 Matchs de ${priorityMatches[0].goalsToWin}`
    : "";

  return (
    <div className="p-3">
      <h3 className="text-slate-50 font-semibold text-sm mb-2">
        ⚡ Prochains matchs {matchFormat && `- ${matchFormat}`}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {priorityMatches.map((match, index) => {
          const isUserMatch = isUserInMatch(match);
          return (
            <div
              key={match.id}
              className={`rounded p-2 flex items-center gap-2 ${
                isUserMatch
                  ? 'bg-blue-500/10 border-2 border-blue-500/50'
                  : 'bg-slate-800'
              }`}
            >
              <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs ${
                isUserMatch ? 'bg-blue-400 text-blue-900' : 'bg-orange-400 text-slate-950'
              }`}>
                {index + 1}
              </div>
              <div className="flex-1 text-xs min-w-0">
                <div className={`truncate ${isUserMatch ? 'text-blue-300 font-semibold' : 'text-slate-300'}`}>
                  {getTeamDisplayName(match, "team1")}
                </div>
                <div className={`truncate ${isUserMatch ? 'text-blue-300 font-semibold' : 'text-slate-300'}`}>
                  {getTeamDisplayName(match, "team2")}
                </div>
              </div>
              <div className={`font-bold text-xs flex-shrink-0 ${isUserMatch ? 'text-blue-400' : 'text-orange-400'}`}>
                VS
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PriorityMatches;